import * as _ from 'lodash'
import React from 'react';
import util from '../util';
import enums from '../enum';
import componentServices from '../componentServices';
import valueFilters from '../valueFilters';
import globalRelay from '../globalRelay';
import * as actions from '../actions';
import jobCostNoteFactory from '../models/jobCostNoteModel';
import { AsyncButton, CurrencyInput, Header, PercentInput, PinnableStats, TooltipTarget } from './sharedComponents';
import { getModel, ModelCollection } from '../store';

const SubtradesSection = ({ summary }) => {
	const subtradeMargin = (util.costCalculator.getMarginWithCostAndSell(summary.subtradeTotalCost, summary.subtradeTotalSellPrice) * 100);
	let keyIndex = 0;
	const subtradeWarrantyRows = _.map(_.filter(summary.subtradeWarrantyItems, item => !item.isPreSubtotal), item => {
		keyIndex++;
		return (<tr key={keyIndex}>
			<td />
			<td>{ item.description }</td>
			<td className="cost">{ util.formatAsCurrency(item.cost) }</td>
			<td className="sell" />
			<td className="sell">{ util.formatAsCurrency(item.sellPrice) }</td>
			<td className="margin" />
			<td />
		</tr>);
	});

	const subtradeTaxRows = _.map(_.filter(summary.subtradeTaxItems, item => !item.isPreSubtotal), item => {
		keyIndex++;
		return (<tr key={keyIndex}>
			<td />
			<td>{ item.description }</td>
			<td className="cost">{ util.formatAsCurrency(item.cost) }</td>
			<td className="sell" />
			<td className="sell">{ util.formatAsCurrency(item.sellPrice) }</td>
			<td className="margin" />
			<td />
		</tr>);
	});

	return summary.jobCost.getLineItemsByType('subtrade').length === 0 ? null : (<section>
		<header>
			<span>SUB TRADES</span>
		</header>
		<div className="inner">
			<table cellPadding="0" cellSpacing="0">
				<thead>
					<tr>
						<td />
						<td>Description</td>
						<td className="cost">Cost</td>
						<td />
						<td className="sell">Sell</td>
						<td className="margin">Margin</td>
						<td />
					</tr>
				</thead>
				<tbody>
					<tr>
						<td />
						<td>Sub Total</td>
						<td className="cost">{ util.formatAsCurrency(summary.subtradeCost) }</td>
						<td />
						<td className="sell">{ util.formatAsCurrency(summary.subtradeSellPrice) }</td>
						<td className="margin" />
						<td />
					</tr>
					{ subtradeWarrantyRows }
					{ subtradeTaxRows }
				</tbody>
				<tfoot className="subtotal">
					<tr>
						<td />
						<td>Sub Trades Total</td>
						<td className="cost" />
						<td />
						<td className="sell">{ util.formatAsCurrency(summary.subtradeTotalPrice) }</td>
						<td className="margin">{ subtradeMargin.toFixed(2) }%</td>
						<td />
					</tr>
				</tfoot>
			</table>
		</div>
	</section>);
};

const SolidSurfaceSection = ({ summary }) => {
	const solidSurfaceCounterMargin = (util.costCalculator.getMarginWithCostAndSell(summary.solidSurfaceCounterTotalCost, summary.solidSurfaceCounterTotalSellPrice) * 100);
	let keyIndex = 0;
	const solidSurfaceCounterWarrantyRows = _.map(_.filter(summary.solidSurfaceCounterWarrantyItems, item => !item.isPreSubtotal), item => {
		keyIndex++;
		return (<tr key={keyIndex}>
			<td />
			<td>{ item.description }</td>
			<td className="cost">{ util.formatAsCurrency(item.cost) }</td>
			<td className="sell" />
			<td className="sell">{ util.formatAsCurrency(item.sellPrice) }</td>
			<td className="margin" />
			<td />
		</tr>);
	});

	const solidSurfaceCounterTaxRows = _.map(_.filter(summary.solidSurfaceCounterTaxItems, item => !item.isPreSubtotal), item => {
		keyIndex++;
		return (<tr key={keyIndex}>
			<td />
			<td>{ item.description }</td>
			<td className="cost">{ util.formatAsCurrency(item.cost) }</td>
			<td className="sell" />
			<td className="sell">{ util.formatAsCurrency(item.sellPrice) }</td>
			<td className="margin" />
			<td />
		</tr>);
	});

	return summary.jobCost.getLineItemsByType('solid-surface-counter').length === 0 ? null : (<section>
		<header>
			<span>SOLID SURFACE COUNTER</span>
		</header>
		<div className="inner">
			<table cellPadding="0" cellSpacing="0">
				<thead>
					<tr>
						<td />
						<td>Description</td>
						<td className="cost">Cost</td>
						<td />
						<td className="sell">Sell</td>
						<td className="margin">Margin</td>
						<td />
					</tr>
				</thead>
				<tbody>
					<tr>
						<td />
						<td>Sub Total</td>
						<td className="cost">{ util.formatAsCurrency(summary.solidSurfaceCounterCost) }</td>
						<td />
						<td className="sell">{ util.formatAsCurrency(summary.solidSurfaceCounterSellPrice) }</td>
						<td className="margin" />
						<td />
					</tr>
					{ solidSurfaceCounterWarrantyRows }
					{ solidSurfaceCounterTaxRows }
				</tbody>
				<tfoot className="subtotal">
					<tr>
						<td />
						<td>Solid Surface Counter Total</td>
						<td className="cost" />
						<td />
						<td className="sell">{ util.formatAsCurrency(summary.solidSurfaceCounterTotalPrice) }</td>
						<td className="margin">{ solidSurfaceCounterMargin.toFixed(2) }%</td>
						<td />
					</tr>
				</tfoot>
			</table>
		</div>
	</section>);
};

const CommissionSection = ({ currentEmployee, job, summary }) => {
	const salesCommissionPercent = summary.jobCost.salesCommissionPercent || currentEmployee.salesCommissionPercent;

	return (job && job.businessSegment === enums.businessSegments.Retail && currentEmployee.key === job.accountManagerKey && salesCommissionPercent) ? (<section>
		<header>
			<span>COMMISSION</span>
		</header>
		<div className="inner">
			<table cellPadding="0" cellSpacing="0">
				<tbody>
					<tr>
						<td />
						<td>Sell (Excludes Tax)</td>
						<td className="sell">{ util.formatAsCurrency(summary.grandTotalSell) }</td>
						<td className="action" />
						<td />
					</tr>
					<tr className="divider">
						<td />
						<td>Cost</td>
						<td className="sell">({ util.formatAsCurrency(summary.grandTotalCost) })</td>
						<td className="action" />
						<td />
					</tr>
					<tr>
						<td />
						<td>Job Total</td>
						<td className="sell">{ util.formatAsCurrency(summary.grandTotalSell - summary.grandTotalCost) }</td>
						<td className="action" />
						<td />
					</tr>
					<tr>
						<td />
						<td>Commission Rate</td>
						<td className="sell">{(currentEmployee.salesCommissionPercent * 100).toFixed(2)}%</td>
						<td className="action" />
						<td />
					</tr>
				</tbody>
				<tfoot className="subtotal">
					<tr>
						<td />
						<td>Commission Balance</td>
						<td className="sell">{ util.formatAsCurrency((summary.grandTotalSell - summary.grandTotalCost) * currentEmployee.salesCommissionPercent) }</td>
						<td className="action"><button tabIndex={-1} className="icon iconOnly info" title="Your Commission Balance will be less any previously paid Design Retainer Commissions"><span /></button></td>
						<td />
					</tr>
				</tfoot>
			</table>
		</div>
	</section>) : null;
};

const UpgradeSummaryRows = ({ summary }) => {
	return (<React.Fragment>
		<tr className="head">
			<td />
			<td>Upgrades</td>
			<td className="cost" />
			<td className="sell" />
			<td className="sell" />
			<td className="margin" />
			<td />
		</tr>
		<tr>
			<td />
			<td>Base Price</td>
			<td className="cost" />
			<td className="sell" />
			<td className="sell">{ util.formatAsCurrency(summary.orderSellPrice) }</td>
			<td className="margin" />
			<td />
		</tr>
		<tr>
			<td />
			<td>Builder Total</td>
			<td className="cost" />
			<td className="sell" />
			<td className="sell">{ util.formatAsCurrency(summary.orderTotalPrice) }</td>
			<td className="margin" />
			<td />
		</tr>
		<tr>
			<td />
			<td>Client Total</td>
			<td className="cost" />
			<td className="sell" />
			<td className="sell">{ util.formatAsCurrency(summary.clientUpgradeSellPrice + summary.clientUpgradeTaxesAndFees) }</td>
			<td className="margin" />
			<td />
		</tr>
	</React.Fragment>);
};

const StandardSummaryRows = ({ orderMargin, orderTotalSell, summary }) => {
	let keyIndex = 0;
	const referralFeePostSubTotalRow = !summary.orderReferralFeePostSubtotalCost ? null : (<tr>
		<td />
		<td>Referral Fee (non-margined)</td>
		<td className="cost" />
		<td className="sell" />
		<td className="sell">{ util.formatAsCurrency(summary.orderReferralFeePostSubtotalCost) }</td>
		<td className="margin" />
		<td />
	</tr>);
	const paymentsRow = !summary.appliedPaymentTotal ? null : (<tr>
		<td />
		<td>Less Prior Payments</td>
		<td className="cost" />
		<td className="sell" />
		<td className="sell">{ util.formatAsCurrency(summary.appliedPaymentTotal) }</td>
		<td className="margin" />
		<td />
	</tr>);
	const orderTaxRows = _.map(_.filter(summary.orderTaxItems, item => !item.isPreSubtotal), item => {
		keyIndex++;
		return (<tr key={keyIndex}>
			<td />
			<td>{ item.description }</td>
			<td className="cost" />
			<td className="sell" />
			<td className="sell">{ util.formatAsCurrency(item.sellPrice) }</td>
			<td className="margin" />
			<td />
		</tr>);
	});
	const orderFeeRows = _.map(_.filter(summary.orderCalculatedFeeItems, item => !item.isPreSubtotal), item => {
		keyIndex++;
		return (<tr key={keyIndex}>
			<td />
			<td>{ item.description }</td>
			<td className="cost" />
			<td className="sell" />
			<td className="sell">{ util.formatAsCurrency(item.sellPrice) }</td>
			<td className="margin" />
			<td />
		</tr>);
	});
	const orderWarrantyRows = _.map(_.filter(summary.orderWarrantyItems, item => !item.isPreSubtotal), item => {
		keyIndex++;
		return (<tr key={keyIndex}>
			<td />
			<td>{ item.description }</td>
			<td className="cost" />
			<td className="sell" />
			<td className="sell">{ util.formatAsCurrency(item.sellPrice) }</td>
			<td className="margin" />
			<td />
		</tr>);
	});

	return (<React.Fragment>
		<tr>
			<td />
			<td>Total Sell</td>
			<td className="cost" />
			<td className="sell" />
			<td className="sell">{ util.formatAsCurrency(orderTotalSell) }</td>
			<td className="margin">{ orderMargin.toFixed(2) }%</td>
			<td />
		</tr>
		{ referralFeePostSubTotalRow }
		{ paymentsRow }
		<tr>
			<td />
			<td>Net Price</td>
			<td className="cost" />
			<td className="sell" />
			<td className="sell">{ util.formatAsCurrency(summary.orderNetPrice) }</td>
			<td className="margin" />
			<td />
		</tr>
		{ orderFeeRows }
		{ orderWarrantyRows }
		{ orderTaxRows }
	</React.Fragment>);
};

const StatsContent = ({ hasUpgrades, summary }) => {
	if (hasUpgrades) {
		return (<React.Fragment>
			<div className="cost column">{ util.formatAsCurrency(summary.grandTotalCost) }<span>COST</span></div>
			<div className="cost column">{ util.formatAsCurrency((summary.orderTotalPrice + summary.clientUpgradeSellPrice + summary.clientUpgradeTaxesAndFees) - (summary.appliedPaymentTotal + summary.orderTaxes)) }<span>PROJECT TOTAL (Excludes Tax)</span></div>
			<div className="cost column">{ util.formatAsCurrency((summary.orderTotalPrice + summary.clientUpgradeSellPrice + summary.clientUpgradeTaxesAndFees) - summary.appliedPaymentTotal) }<span>CONTRACT (Includes Tax)</span></div>
			<div className="cost column">{ (util.costCalculator.getMarginWithCostAndSell(summary.grandTotalCost, summary.orderTotalSellWithUpgrades) * 100).toFixed(2) }%<span>AVERAGE MARGIN</span></div>
		</React.Fragment>);
	}
	else {
		return (<React.Fragment>
			<div className="cost column">{ util.formatAsCurrency(summary.grandTotalCost) }<span>COST</span></div>
			<div className="cost column">{ util.formatAsCurrency(summary.grandTotalSell) }<span>SELL (Excludes Tax)</span></div>
			<div className="cost column">{ util.formatAsCurrency(summary.grandTotalContract) }<span>CONTRACT (Includes Tax)</span></div>
			<div className="cost column">{ (util.costCalculator.getMarginWithCostAndSell(summary.grandTotalCost, summary.grandTotalSell) * 100).toFixed(2) }%<span>AVERAGE MARGIN</span></div>
		</React.Fragment>);
	}
};

export default class JobCostEditSummary extends React.Component<any,any> {
	constructor (props) {
		super(props);

		this.printReport = this.printReport.bind(this);
		this.pinStats = this.pinStats.bind(this);
	}

	printReport () {
		const { fetchReport } = this.props;

		return fetchReport('internal/jobcostsummary');
	}

	updateNote (autoSave, e) {
		const newVal = autoSave ? valueFilters.trim(e.target.value) : e.target.value;

		this.props.updateModel(jobCost => {
			let note = jobCost.getNote(jobCostNoteFactory.NOTE_TYPES.CONTRACT_CUSTOMER_PRICING);

			if (!note) {
				note = jobCostNoteFactory({ type: jobCostNoteFactory.NOTE_TYPES.CONTRACT_CUSTOMER_PRICING });
				jobCost.notes.add(note);
			}

			note.content = newVal;
		}, { autoSave: autoSave });
	}

	updateAdjustedSell (autoSave, e) {
		e.preventDefault();

		const newVal = (autoSave) ? valueFilters.clean(e.target.value) : e.target.value;

		this.props.updateModel(jobCost => {
			const updateTarget = jobCost.currentChangeOrder || jobCost;

			updateTarget.adjustedSellPrice = newVal;
		}, { autoSave: autoSave });
	}

	updateFinalMargin (autoSave, e) {
		const { summary } = this.props;

		e.preventDefault();

		const newVal = (autoSave) ? valueFilters.clean(e.target.value) : e.target.value;

		if (newVal === 0) { return; } // eslint-disable-line eqeqeq

		this.props.updateModel(jobCost => {
			const updateTarget = jobCost.currentChangeOrder || jobCost;

			updateTarget.adjustedSellPrice = util.roundAsCurrency(util.costCalculator.getSellWithCostAndMargin(summary.orderTotalCost, newVal));
		}, { autoSave: autoSave });
	}

	pinStats () {
		actions.setPinnedStatState({ key: 'jcSummary', isPinned: true });
	}

	render () {
		const { securityContext, store } = componentServices;
		const { summary, jobCost } = this.props;

		if (!summary) {
			return null;
		}
		const globalConfig = globalRelay.config.withVariant(jobCost.companyVariant);
		const supportsInstallation = globalConfig.jobCostSettings.installation.mode !== 'none';
		const titlebarActions = [<AsyncButton className="pdf" asyncAction={ this.printReport } label="Print" />];

		let keyIndex = 0;
		const hasPinnedStats = !!store.getIn(['viewState', 'pinnedStats', 'jcSummary']);
		const job = getModel({ store, collection: ModelCollection.jobs, key: jobCost.jobKey });
		const currentEmployee = securityContext.getCurrentEmployee();
		const isLocked = summary.jobCost.allowedContractEdits === 'none';
		const orderTotalSell = summary.orderSellPrice;
		const orderMargin = (util.costCalculator.getMarginWithCostAndSell(summary.orderTotalCost, orderTotalSell) * 100);
		const hasUpgrades = (summary.builderUpgradeSellPrice + summary.clientUpgradeSellPrice) > 0;

		const referralFeeRow = !summary.orderReferralFeeCost ? null : (<tr>
			<td />
			<td>Referral Fee (margined)</td>
			<td className="cost">{ util.formatAsCurrency(summary.orderReferralFeeCost) }</td>
			<td className="sell">{ util.formatAsCurrency(summary.orderReferralFeeCost) }</td>
			<td className="sell" />
			<td className="margin" />
			<td />
		</tr>);

		const orderWarrantyPreRows = _.map(_.filter(summary.orderWarrantyItems, { isPreSubtotal: true }), item => {
			keyIndex++;
			return (<tr key={ 'row' + keyIndex}>
				<td />
				<td>{ item.description }</td>
				<td className="cost">{ util.formatAsCurrency(item.cost) }</td>
				<td className="sell">{ util.formatAsCurrency(item.sellPrice) }</td>
				<td className="sell" />
				<td className="margin" />
				<td />
			</tr>);
		});

		const orderFeePreRows = _.map(_.filter(summary.orderCalculatedFeeItems, { isPreSubtotal: true }), item => {
			keyIndex++;
			return (<tr key={ 'row' + keyIndex}>
				<td />
				<td>{ item.description }</td>
				<td className="cost">{ util.formatAsCurrency(item.cost) }</td>
				<td className="sell">{ util.formatAsCurrency(item.sellPrice) }</td>
				<td className="sell" />
				<td className="margin" />
				<td />
			</tr>);
		});

		const orderTaxPreRows = []; /*_.map(_.filter(summary.orderTaxItems, { isPreSubtotal: true }), item => {
			keyIndex++;
			return (<tr key={ 'row' + keyIndex}>
						<td></td>
						<td>{ item.description }</td>
						<td className="cost">{ util.formatAsCurrency(item.cost) }</td>
						<td className="sell">{ util.formatAsCurrency(item.sellPrice) }</td>
						<td className="sell"></td>
						<td className="margin"></td>
						<td></td>
					</tr>);
		}); */

		// And finally, we return
		return (
			<section>
				<Header title="Summary" actions={ titlebarActions } />
				<div className="jobCostSummary">
					<div className="inner">

						<table cellPadding="0" cellSpacing="0">
							<thead>
								<tr>
									<td />
									<td>Description</td>
									<td className="cost">Cost</td>
									<td className="sell">Calculated Sell</td>
									<td className="sell" />
									<td className="margin">Margin</td>
									<td />
								</tr>
							</thead>
							<tbody>
								<tr>
									<td />
									<td>Cabinet Cost &amp; Freight</td>
									<td className="cost">{ util.formatAsCurrency(summary.orderCabinetCost) }</td>
									<td className="sell">{ util.formatAsCurrency(summary.orderCabinetSell) }</td>
									<td className="sell" />
									<td className="margin">{ (util.costCalculator.getMarginWithCostAndSell(summary.orderCabinetCost, summary.orderCabinetSell) * 100).toFixed(2)}%</td>
									<td />
								</tr>
								<tr>
									<td />
									<td>Accessories &amp; Mods</td>
									<td className="cost">{ util.formatAsCurrency(summary.orderAccessoryModCost) }</td>
									<td className="sell">{ util.formatAsCurrency(summary.orderAccessoryModSell) }</td>
									<td className="sell" />
									<td className="margin">{ (util.costCalculator.getMarginWithCostAndSell(summary.orderAccessoryModCost, summary.orderAccessoryModSell) * 100).toFixed(2)}%</td>
									<td />
								</tr>
								<tr>
									<td />
									<td>Glass</td>
									<td className="cost">{ util.formatAsCurrency(summary.orderGlassCost + summary.orderSpecialGlassCost) }</td>
									<td className="sell">{ util.formatAsCurrency(summary.orderGlassSell + summary.orderSpecialGlassSell) }</td>
									<td className="sell" />
									<td className="margin">{ (util.costCalculator.getMarginWithCostAndSell(summary.orderGlassCost + summary.orderSpecialGlassCost, summary.orderGlassSell + summary.orderSpecialGlassSell) * 100).toFixed(2)}%</td>
									<td />
								</tr>
								{ supportsInstallation ? <tr>
									<td />
									<td>Installation</td>
									<td className="cost">{ util.formatAsCurrency(summary.orderInstallationCost) }</td>
									<td className="sell">{ util.formatAsCurrency(summary.orderInstallationSell) }</td>
									<td className="sell" />
									<td className="margin">{ (util.costCalculator.getMarginWithCostAndSell(summary.orderInstallationCost, summary.orderInstallationSell) * 100).toFixed(2)}%</td>
									<td />
								</tr> : null }
								{ supportsInstallation ? <tr>
									<td />
									<td>Install Service</td>
									<td className="cost">{ util.formatAsCurrency(summary.orderInstallServiceCost) }</td>
									<td className="sell">{ util.formatAsCurrency(summary.orderInstallServiceSell) }</td>
									<td className="sell" />
									<td className="margin">{ (util.costCalculator.getMarginWithCostAndSell(summary.orderInstallServiceCost, summary.orderInstallServiceSell) * 100).toFixed(2)}%</td>
									<td />
								</tr> : null }
								{ supportsInstallation ? <tr>
									<td />
									<td>Travel</td>
									<td className="cost">{ util.formatAsCurrency(summary.orderTravelCost) }</td>
									<td className="sell">{ util.formatAsCurrency(summary.orderTravelSell) }</td>
									<td className="sell" />
									<td className="margin">{ (util.costCalculator.getMarginWithCostAndSell(summary.orderTravelCost, summary.orderTravelSell) * 100).toFixed(2)}%</td>
									<td />
								</tr> : null }
								<tr className="divider">
									<td />
									<td>Delivery</td>
									<td className="cost">{ util.formatAsCurrency(summary.orderDeliveryCost) }</td>
									<td className="sell">{ util.formatAsCurrency(summary.orderDeliverySell) }</td>
									<td className="sell" />
									<td className="margin">{ (util.costCalculator.getMarginWithCostAndSell(summary.orderDeliveryCost, summary.orderDeliverySell) * 100).toFixed(2)}%</td>
									<td />
								</tr>
								<tr className="head">
									<td />
									<td>Description</td>
									<td className="cost">Cost</td>
									<td className="sell">Calculated Sell</td>
									<td className="sell">Sell</td>
									<td className="margin">Margin</td>
									<td />
								</tr>
								{ referralFeeRow }
								{ orderFeePreRows }
								{ orderWarrantyPreRows }
								{ orderTaxPreRows }
								<tr className={ isLocked ? 'divider summarySell readOnly' : 'divider summarySell' }>
									<td />
									<td>{ hasUpgrades ? 'Base Price' : 'Subtotal' }</td>
									<td className="cost">{ util.formatAsCurrency(summary.orderTotalCost) }</td>
									<td className="sell">{ util.formatAsCurrency(summary.orderCaculatedSellPrice) }</td>
									<td className="sell"><CurrencyInput className={ isLocked ? 'disabled' : '' } placeholder="0.00" value={ summary.orderSellPrice } onChange={ this.updateAdjustedSell.bind(this, false) } onBlur={ this.updateAdjustedSell.bind(this, true) } /></td>
									<td className="margin"><PercentInput className={ isLocked ? 'disabled' : '' } placeholder="0" value={ orderMargin / 100 } onChange={ this.updateFinalMargin.bind(this, false) } onBlur={ this.updateFinalMargin.bind(this, true) } /></td>
									<td />
								</tr>
								{ hasUpgrades ? <UpgradeSummaryRows summary={ summary } />
									: <StandardSummaryRows orderMargin={ orderMargin } orderTotalSell={ orderTotalSell } summary={ summary } />
								}

							</tbody>
							<tfoot className="subtotal">
								<tr>
									<td />
									<td>{hasUpgrades ? 'Project Total' : 'Order Total'}</td>
									<td className="cost" />
									<td />
									<td className="sell">{ util.formatAsCurrency((summary.orderTotalPrice + summary.clientUpgradeSellPrice + summary.clientUpgradeTaxesAndFees)) }</td>
									<td className="margin" />
									<td />
								</tr>
							</tfoot>
						</table>

					</div>

					<SubtradesSection summary={ summary } />
					<SolidSurfaceSection summary={ summary } />

					{ job && job.businessSegment === enums.businessSegments.Retail ? <section>
						<header>
							<span>CONTRACT NOTES FOR CLIENT<TooltipTarget text="Notes will be displayed on the customer contract"><button tabIndex={-1} className="icon iconOnly info"><span /></button></TooltipTarget></span>
						</header>
						<div className="inner">
							<table className="compact" cellPadding="0" cellSpacing="0">
								<tbody>
									<tr className={ isLocked ? 'readOnly' : '' }>
										<td><textarea 
											style={isLocked ? {pointerEvents: 'stroke'} :{}}
											value={ jobCost.getNoteContent(jobCostNoteFactory.NOTE_TYPES.CONTRACT_CUSTOMER_PRICING)} 
											onChange={ this.updateNote.bind(this, false)} 
											onBlur={ this.updateNote.bind(this, true)} 
											placeholder="Enter notes to be displayed on the Customer Pricing and Summary Reports" 
											readOnly={isLocked}
										/></td>
									</tr>
								</tbody>
							</table>
						</div>
					</section> : null }

					<CommissionSection currentEmployee={ currentEmployee } job={ job } summary={ summary } />

					{ hasPinnedStats ? <section className="stats total pinnable out" /> : null }
					<PinnableStats isPinned={ hasPinnedStats }>
						<section className={ hasPinnedStats ? 'stats total compact' : 'stats total pinnable'}>
							{ hasPinnedStats ? null : <button className="icon iconOnly pinItem" onClick={ this.pinStats }><span /></button> }
							<StatsContent hasUpgrades={ hasUpgrades} summary={ summary} />
						</section>
					</PinnableStats>
				</div>
			</section>
		);
	}
}
